import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Stars, Badge } from "@/lib/ui";
import { CalendarClock, Send, Check } from "lucide-react";
import TrustBadges from "@/components/TrustBadges";
import { DistanceTag } from "@/components/LocationFilter";
import Avatar from "@/components/Avatar";

export default function FreeHelpers({ requestId, scheduledAt }) {
  const [helpers, setHelpers] = useState(null);
  const load = () => api.get(`/requests/${requestId}/free-helpers`, { params: { tz_offset: new Date().getTimezoneOffset() } }).then((r) => setHelpers(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [requestId]);

  const invite = async (h) => {
    try {
      await api.post(`/requests/${requestId}/invite`, { helper_id: h.id });
      toast.success(`Invitation sent to ${h.name}`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (!helpers) return null;
  const when = new Date(scheduledAt).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  return (
    <section data-testid="free-helpers" className="mt-8 pt-6 border-t border-[#E2D9CE]">
      <div className="flex items-center gap-2 mb-1"><CalendarClock className="w-6 h-6 text-[#2E7D6B]" /><h3 className="text-xl font-semibold text-slate-900">Helpers free at this time</h3></div>
      <p className="text-slate-600 mb-4">{when}. Invite someone directly — they'll see it on their dashboard.</p>
      {helpers.length === 0 ? (
        <p data-testid="free-helpers-empty" className="text-slate-600 bg-[#FAF8F5] rounded-xl p-4">No helpers have marked themselves free at this slot yet. Your request is still visible to everyone.</p>
      ) : (
        <div className="space-y-3">
          {helpers.map((h) => (
            <div key={h.id} data-testid={`free-helper-${h.id}`} className="flex flex-wrap items-center justify-between gap-3 bg-[#FAF8F5] rounded-xl p-4">
              <Link to={`/helpers/${h.id}`} className="flex items-center gap-3">
                <Avatar user={h} size="sm" />
                <div>
                  <p className="font-semibold text-slate-900">{h.name}</p>
                  <div className="flex flex-wrap items-center gap-2"><Stars rating={h.avg_rating} size="text-sm" /><TrustBadges user={h} /><DistanceTag km={h.distance_km} /></div>
                </div>
              </Link>
              {h.invited ? <Badge color="green" testid={`invited-${h.id}`}><Check className="w-4 h-4" /> Invited</Badge> : (
                <button data-testid={`invite-helper-${h.id}`} onClick={() => invite(h)}
                  className="flex items-center gap-2 bg-[#2E7D6B] hover:bg-[#256658] text-white font-semibold px-4 min-h-[44px] rounded-xl transition-colors">
                  <Send className="w-4 h-4" /> Invite
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
